export const MAIN_CONTENT_ID = 'main-content';
export const KNOVIO_EMBED_CLASS = 'KnowledgeVisionEmbeddedContent';
export const KNOVIO_FOCUS_PLAY_CONTROL_MESSAGE = 'focusPlayControl';

const isElementVisible = (element: HTMLElement): boolean => {
  if (element.getAttribute('aria-hidden') === 'true') {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
};

const isEventDetailsHeading = (element: HTMLElement): boolean => {
  const className = typeof element.className === 'string' ? element.className : '';
  return className.includes('event-details--name')
    || Boolean(element.closest('[class*="-event-details"]'));
};

const focusElement = (element: HTMLElement): boolean => {
  const isNativelyFocusable = /^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/i.test(element.tagName)
    || element.isContentEditable;
  if (!isNativelyFocusable && !element.hasAttribute('tabindex')) {
    element.tabIndex = -1;
  }
  element.focus({ preventScroll: true });
  if (typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ block: 'start', behavior: 'instant' as ScrollBehavior });
  }
  return document.activeElement === element;
};

const findKnovioIframes = (): HTMLIFrameElement[] => {
  const selectors = [
    `.${KNOVIO_EMBED_CLASS} iframe`,
    'iframe[id*="KnowledgeVisionEmbeddedContent"]',
    'iframe[src*="knowledgevision"]',
  ];
  const iframes = new Set<HTMLIFrameElement>();
  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLIFrameElement) {
        iframes.add(node);
      }
    });
  });
  return Array.from(iframes);
};

const requestKnovioIframeFocusPlayControl = (iframe: HTMLIFrameElement): boolean => {
  if (!iframe.contentWindow) {
    return false;
  }
  if (!iframe.hasAttribute('tabindex')) {
    iframe.tabIndex = -1;
  }
  iframe.focus({ preventScroll: true });
  iframe.contentWindow.postMessage(
    JSON.stringify({ message: KNOVIO_FOCUS_PLAY_CONTROL_MESSAGE }),
    '*'
  );
  return true;
};

export const findContentModalHeading = (): HTMLElement | null => {
  const modal = document.querySelector(
    'main .shadow-gray.bg-white, main .editable-session--wrapper'
  ) as HTMLElement | null;
  if (modal) {
    const heading = modal.querySelector('h1, h2') as HTMLElement | null;
    if (heading && isElementVisible(heading) && !isEventDetailsHeading(heading)) {
      return heading;
    }
  }

  const headings = Array.from(document.querySelectorAll('main h1, main h2')) as HTMLElement[];
  return headings.find((heading) => isElementVisible(heading) && !isEventDetailsHeading(heading)) || null;
};

export const focusMainContent = (): void => {
  const iframes = findKnovioIframes();
  for (const iframe of iframes) {
    if (requestKnovioIframeFocusPlayControl(iframe)) {
      return;
    }
  }

  const heading = findContentModalHeading();
  if (heading && focusElement(heading)) {
    return;
  }

  const main = document.getElementById(MAIN_CONTENT_ID);
  if (main) {
    focusElement(main);
  }
};

export const prepareSkipLinkAsNextTabTarget = (): void => {
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body) {
    active.blur();
  }
  if (!document.body.hasAttribute('tabindex')) {
    document.body.tabIndex = -1;
  }
  document.body.focus({ preventScroll: true });
};
